import { Play } from "lucide-react";

// Last-resort fallback poster for ASMR reels — a known-stable Unsplash soap
// macro that is verified to load. Used only if a reel's primary poster fails.
const FALLBACK_POSTER =
    "https://images.unsplash.com/photo-1556228453-efd6c1ff04f6?auto=format&fit=crop&w=1000&q=80";

const REELS = [
    {
        category: "Soap Cutting",
        title: "Gold Loaf Cut",
        subtitle: "Turmeric swirl · Day 42",
        poster: "https://images.unsplash.com/photo-1601049541289-9b1b7bbbfe19?auto=format&fit=crop&w=1000&q=80",
        duration: "0:38",
    },
    {
        category: "Lather Sounds",
        title: "Creamy Pour Lather",
        subtitle: "Binaural foam",
        poster: "https://images.unsplash.com/photo-1515378791036-0648a3ef77b2?auto=format&fit=crop&w=1000&q=80",
        duration: "0:44",
    },
    {
        category: "Unboxing",
        title: "Glow Set Reveal",
        subtitle: "Wrapped in banana leaf",
        poster: "https://images.unsplash.com/photo-1617897903246-719242758050?auto=format&fit=crop&w=1000&q=80",
        duration: "0:52",
    },
    {
        category: "Spa & Shower",
        title: "Rain Shower · Gold Bar",
        subtitle: "Water, steam, aloha",
        poster: "https://images.unsplash.com/photo-1556228578-8c89e6adf883?auto=format&fit=crop&w=1000&q=80",
        duration: "1:02",
    },
    {
        category: "Product Showcase",
        title: "Crystal in Sunlight",
        subtitle: "Cube embeds · macro",
        poster: "https://images.unsplash.com/photo-1612817288484-6f916006741a?auto=format&fit=crop&w=1000&q=80",
        duration: "0:29",
    },
];

export default function ASMRGallery() {
    return (
        <section
            className="relative bg-forest text-cream py-20 md:py-28 overflow-hidden"
            data-testid="asmr-gallery"
        >
            <div className="grain absolute inset-0" />
            <div className="relative max-w-7xl mx-auto px-5 md:px-10">
                <div className="flex items-end justify-between flex-wrap gap-4 mb-10">
                    <div>
                        <span className="divider-rule text-gold">
                            ASMR Experience
                        </span>
                        <h2 className="font-serif text-4xl sm:text-5xl lg:text-6xl mt-5 leading-[1.02]">
                            The sound of
                            <br />
                            <span className="italic text-gold">
                                slow island craft.
                            </span>
                        </h2>
                    </div>
                    <p className="max-w-xs text-sm text-cream/70 leading-relaxed">
                        Pouring, curing, slicing. Every loaf is cut by hand on
                        camera. Tap any reel — no sound? Headphones on, please.
                    </p>
                </div>

                <div className="flex gap-5 overflow-x-auto no-scrollbar snap-x snap-mandatory pb-6 -mx-5 px-5 md:mx-0 md:px-0">
                    {REELS.map((r, i) => (
                        <article
                            key={i}
                            className="relative shrink-0 w-[260px] sm:w-[300px] aspect-[9/14] rounded-sm overflow-hidden snap-start group cursor-pointer"
                            data-testid={`asmr-reel-${i}`}
                        >
                            <img
                                src={r.poster}
                                alt={r.title}
                                loading="lazy"
                                onError={(e) => {
                                    if (e.currentTarget.src !== FALLBACK_POSTER) {
                                        e.currentTarget.src = FALLBACK_POSTER;
                                    }
                                }}
                                className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-[1.07]"
                            />
                            <div
                                className="absolute inset-0 bg-gradient-to-t from-forest-deep/90 via-forest/30 to-transparent"
                                aria-hidden
                            />
                            <div className="absolute top-4 left-4 text-[0.6rem] tracking-[0.3em] uppercase text-cream/80 bg-forest/60 px-2.5 py-1 rounded-full">
                                {r.duration}
                            </div>
                            <div className="absolute inset-0 flex items-center justify-center">
                                <div className="w-14 h-14 rounded-full bg-gold/90 text-forest flex items-center justify-center transition-transform duration-500 group-hover:scale-110">
                                    <Play
                                        size={22}
                                        fill="currentColor"
                                        className="translate-x-0.5"
                                    />
                                </div>
                            </div>
                            <div className="absolute bottom-0 left-0 right-0 p-5">
                                <p className="text-[0.6rem] tracking-[0.3em] uppercase text-gold">
                                    {r.category}
                                </p>
                                <h4 className="font-serif text-xl mt-1">
                                    {r.title}
                                </h4>
                                <p className="text-[0.7rem] text-cream/70 mt-1">
                                    {r.subtitle}
                                </p>
                            </div>
                        </article>
                    ))}
                    <div className="shrink-0 w-4" aria-hidden />
                </div>

                <p className="mt-4 text-[0.65rem] tracking-[0.3em] uppercase text-cream/50">
                    Scroll →
                </p>
            </div>
        </section>
    );
}
